import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

// Status light indicator
const StatusDot = styled('span')(({ connected, theme }) => ({
  display: 'inline-block',
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: connected ? '#4CAF50' : '#F44336', // Green when connected, red when not
  boxShadow: connected ? '0 0 4px 1px rgba(76, 175, 80, 0.7)' : '0 0 4px 1px rgba(244, 67, 54, 0.7)',
  marginRight: 4
}));

// Use shouldForwardProp to exclude testnetMode from being forwarded to DOM
const WalletChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'testnetMode'
})(({ theme, testnetMode }) => ({
  borderRadius: 16,
  backgroundColor: testnetMode ? '#FFF8E1' : '#E8F5E9', // Light background color based on network
  border: `1px solid ${testnetMode ? '#FFB74D' : '#81C784'}`,
  '& .MuiChip-icon': {
    color: testnetMode ? '#F57C00' : '#4CAF50',
  },
  height: 28,
  fontSize: 12,
  cursor: 'pointer',
}));

const WalletStatusChip = ({ 
  isConnected, 
  address, 
  networkName,
  tokenSymbol,
  isTestnet,  // Keep this prop name unchanged for backward compatibility
  walletType = 'Wallet',
  balance
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  
  // Don't render if not connected
  if (!isConnected) return null;
  
  // Truncate wallet address for display
  const truncatedAddress = address ? 
    `${address.substring(0, 4)}...${address.substring(address.length - 4)}` : 
    '';
  
  // Format balance properly to avoid NaN
  const formatBalance = () => {
    if (!balance) return null;
    
    // If balance is an object with formatted property
    if (typeof balance === 'object' && balance.formatted) {
      return parseFloat(balance.formatted).toFixed(4);
    }
    
    // If balance is already a string or number
    if (typeof balance === 'string' || typeof balance === 'number') {
      const balanceNum = parseFloat(balance);
      return isNaN(balanceNum) ? null : balanceNum.toFixed(4);
    }
    
    return null;
  };
  
  const formattedBalance = formatBalance();
  
  // Create tooltip content
  const tooltipContent = (
    <Paper 
      sx={{ 
        p: 1.5, 
        maxWidth: 260,
        backgroundColor: 'rgba(0, 0, 0, 0.05)', // Light grey transparent background matching typical border
        color: 'inherit',
        boxShadow: 1
      }}
    >
      <Typography variant="subtitle2" fontWeighst="bold">{walletType} Connected</Typography>
      <Typography variant="body2">Network: {networkName}</Typography>
      <Typography variant="body2">Address: {address ? `${address.substring(0, 6)}...${address.substring(address.length - 4)}` : ''}</Typography>
      {formattedBalance && (
        <Typography variant="body2">
          Balance: {formattedBalance} {tokenSymbol}
        </Typography>
      )}
      {isTestnet && (
        <Typography variant="body2" color="warning.main" fontWeight="bold">
          Testnet Mode Active
        </Typography>
      )}
    </Paper>
  );

  return (
    <Tooltip
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      title={tooltipContent}
      placement="left-start"
      arrow
      PopperProps={{
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              boundary: document.querySelector('.payment-card') || document.body,
              padding: 12
            }
          }
        ]
      }}
    >
      <WalletChip
        icon={
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={<StatusDot connected={isConnected} />}
          >
            <AccountBalanceWalletIcon fontSize="small" />
          </Badge>
        }
        label={truncatedAddress || "Connected"}
        testnetMode={isTestnet}
        size="small"
        sx={{ cursor: 'pointer' }}
      />
    </Tooltip>
  );
};

export default WalletStatusChip;